import React, { useRef, useState } from 'react';
import './FileInput.css';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf, faSpinner, faXmark } from '@fortawesome/free-solid-svg-icons';

const backendBaseUrl = (process.env.REACT_APP_URL_BACK || 'http://localhost:8000').replace(/\/$/, '');
const maxFileSize = 50 * 1024 * 1024;

const FileInput = React.forwardRef(({
  onFileSelect,
  onFileRemove,
  onUpload,
  isActive,
  additionalClass,
  userEmail,
}, ref) => {
  const inputRef = useRef();
  const [selectedFile, setSelectedFile] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  const handleOnChange = (event) => {
    const file = event.target.files?.[0];
    setErrorMessage('');

    if (!file) {
      setSelectedFile(null);
      return;
    }

    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      setErrorMessage('Selecione um arquivo PDF válido.');
      setSelectedFile(null);
      return;
    }

    if (file.size > maxFileSize) {
      setErrorMessage('O arquivo deve ter no máximo 50 MB.');
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
    onFileSelect?.(file);
  };

  const onChooseFile = () => inputRef.current?.click();

  React.useImperativeHandle(ref, () => ({ onChooseFile }));

  const removeFile = () => {
    setSelectedFile(null);
    setErrorMessage('');
    if (inputRef.current) inputRef.current.value = '';
    onFileRemove?.();
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setErrorMessage('Selecione um documento antes de enviar.');
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('user_email', userEmail || 'anonymous');
    setIsUploading(true);
    setErrorMessage('');

    try {
      const response = await axios.post(`${backendBaseUrl}/pdf`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (!response.data?.document_id) {
        throw new Error(response.data?.feedback || 'O backend não indexou o documento.');
      }

      onUpload?.(response.data);
      setSelectedFile(null);
      if (inputRef.current) inputRef.current.value = '';
    } catch (error) {
      const detail = error.response?.data?.detail || error.response?.data?.feedback || error.message;
      setErrorMessage(detail || 'Erro ao enviar o documento.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className={`file ${additionalClass ? 'file-floating' : ''}`}>
      <input
        type="file"
        accept=".pdf,application/pdf"
        ref={inputRef}
        onChange={handleOnChange}
        className="file-native-input"
      />

      {errorMessage && <p className="file-error" role="alert">{errorMessage}</p>}

      {selectedFile && (
        <div className={`selected-file ${isActive ? 'file-active' : ''}`}>
          <span className="selected-file-icon"><FontAwesomeIcon icon={faFilePdf} /></span>
          <div className="selected-file-copy">
            <strong>{selectedFile.name}</strong>
            <span>{(selectedFile.size / 1024 / 1024).toFixed(1)} MB</span>
          </div>
          <button disabled={isUploading} onClick={removeFile} className="file-cancel" type="button" aria-label="Remover arquivo">
            <FontAwesomeIcon icon={faXmark} />
          </button>
          <button disabled={isUploading} onClick={handleUpload} className="file-submit" type="button">
            {isUploading ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Enviar'}
          </button>
        </div>
      )}
    </div>
  );
});

export default FileInput;
